import {
  getSubscription,
  startSubscription,
  updateSubscription,
  updateSubscriptionPoNumber,
} from "src/billinglatest/clients/service.client";
import type {
  BillingCycle,
  CurrencyCode,
  PlanId,
} from "../PlanBillingStep/constants";
import type { PixiAddress } from "../../hooks/usePixiWizardState";
import { pixiAddressToBillingAddress } from "./mappers/checkoutPayload";

/**
 * The subset of the purchase order needed to set the plan and activate the
 * subscription (steps 3–5 of the Pixi purchase). Mirrors the relevant fields of
 * `PurchaseInput`.
 *
 * `currency` is optional so the no-fallback currency guard still applies here:
 * Chargebee locks the currency to the customer, so if it's missing we fail
 * rather than charge a currency the customer never agreed to.
 */
export interface PixiPurchaseOrder {
  /** Plan tier group, e.g. "core" | "pro". */
  planGroup: PlanId;
  /** Billing cycle, "year" | "month". */
  billingCycle: BillingCycle;
  /** The trialist's locked billing currency, resolved upstream. No fallback. */
  currency?: CurrencyCode;
  licenses: number;
  /**
   * Set for the free-device deal: activates the subscription as a PIXI bundle
   * (adds the free device + paid shipping). The backend resolves the shipping
   * cost from its rate table using the address country + service.
   */
  bundle?: {
    shippingAddress: PixiAddress;
    shippingService: string;
  };
  /**
   * Optional PO number applied to invoices (GRWBKK-1704). Unvalidated, so it is
   * applied best-effort AFTER the subscription is active and never fails the
   * purchase. Carried on the order so both the inline and the redirect-resume
   * paths (whose only shared step is this module) apply it identically.
   */
  poNumber?: string;
}

/**
 * The user-facing default text thrown when the billing currency is missing.
 * Kept identical to usePurchase's `pixi_wizard.checkout.error.currency_missing`
 * default. Callers that need the translated string may pass
 * `opts.currencyMissingMessage`; otherwise this default (matching the existing
 * copy) is used. This keeps `finalizePurchase` a pure, React-free module while
 * preserving the exact user-facing error text.
 */
export const CURRENCY_MISSING_MESSAGE =
  "We couldn't confirm your billing currency. Please try again.";

export interface FinalizePurchaseOptions {
  /** Space id scoping the billing SDK calls. */
  spaceId?: string;
  /** Optional translated override for the currency-missing error text. */
  currencyMissingMessage?: string;
}

/**
 * Steps 3–6 of the Pixi purchase, extracted verbatim as a pure async function:
 *
 * Step 3 — currency guard (no fallback) → build the plan id.
 * Step 4 — set the chosen plan + licences (updateSubscription), unless the
 * subscription is cancelled, where that call would reactivate and bill it.
 * Step 5 — activate the subscription (startSubscription), passing the PIXI
 * bundle for the free-device deal and the selection step 4 skipped.
 * Step 6 — apply the optional PO number (best-effort; never fails the purchase).
 *
 * This module never touches Stripe.
 */
export const finalizePurchase = async (
  order: PixiPurchaseOrder,
  opts: FinalizePurchaseOptions = {},
): Promise<void> => {
  const { spaceId, currencyMissingMessage } = opts;

  // 3. Build the plan id from the trialist's locked currency (resolved
  // upstream). No fallback: fail rather than charge a currency the customer
  // never agreed to. group/cycle are the user's choice; only currency is
  // constrained by Chargebee.
  if (!order.currency) {
    throw new Error(currencyMissingMessage ?? CURRENCY_MISSING_MESSAGE);
  }
  const planId = `${order.planGroup}-${order.billingCycle}-${order.currency.toLowerCase()}`;

  // 4. Set the plan + licences, except on a cancelled subscription: updating one
  // reactivates AND invoices it, billing at the catalog price before step 5 can
  // apply the campaign price (GRWBKK-1963).
  //
  // Read fresh, not from the order: the redirect-resume path replays an order
  // persisted before the gateway hop. `getSubscription` yields `{}` on error, so
  // an unreadable status keeps the old update-then-start path.
  const subscription = await getSubscription(spaceId);
  const isCancelled = subscription.status === "cancelled";

  // `paymentIntentId` is always null: the card is authenticated on its SetupIntent
  // when attached and Chargebee charges off-session in step 5.
  if (!isCancelled) {
    await updateSubscription(
      { planId, quantity: order.licenses, paymentIntentId: null },
      spaceId,
    );
  }

  // 5. Activate. The free-device deal passes the bundle option + shipping; a
  // cancelled subscription also carries the selection step 4 skipped.
  const startRequest: NonNullable<Parameters<typeof startSubscription>[0]> = {
    ...(isCancelled ? { planId, quantity: order.licenses } : {}),
    ...(order.bundle
      ? {
          option: "pixi" as const,
          shippingAddress: pixiAddressToBillingAddress(
            order.bundle.shippingAddress,
          ),
          shippingService: order.bundle.shippingService,
        }
      : {}),
  };
  // A plain trial conversion sends no body at all.
  const hasStartRequest = Object.keys(startRequest).length > 0;
  await startSubscription(hasStartRequest ? startRequest : undefined, spaceId);

  // 6. Apply the optional PO number to the now-active subscription. This is an
  // unvalidated invoice reference, so it runs AFTER the purchase has fully
  // succeeded and is best-effort: a failure here must never report the
  // (already-charged, already-activated) purchase as failed. Swallow it.
  if (order.poNumber?.trim()) {
    try {
      await updateSubscriptionPoNumber(
        { poNumber: order.poNumber.trim() },
        spaceId,
      );
    } catch {
      // Non-fatal: the purchase is complete; the PO can be set later.
    }
  }
};
